import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { ArrowLeftRight, AlertTriangle } from "lucide-react";
import { api } from "@/lib/api";
import { fmtDate } from "@/lib/format";

const REASONS = ["Delivery Partner Request", "Location Change", "Franchise Partner Request", "Operational Requirement", "Other"];

// Effective-date default: today in the CRM timezone (Asia/Kolkata)
const istToday = () => new Date(Date.now() + 5.5 * 3600 * 1000).toISOString().slice(0, 10);

export function TransferFranchiseModal({ dp, open, onClose, onSaved }) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [fr, setFr] = useState(null);
  const [effective, setEffective] = useState(istToday());
  const [reason, setReason] = useState(REASONS[0]);
  const [customReason, setCustomReason] = useState("");
  const [remarks, setRemarks] = useState("");
  const [confirmBackdated, setConfirmBackdated] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setFr(null); setQuery(""); setResults([]);
      setEffective(istToday()); setReason(REASONS[0]); setCustomReason("");
      setRemarks(""); setConfirmBackdated(false);
    }
  }, [open]);

  useEffect(() => {
    const t = setTimeout(() => {
      if (query) api.get(`/franchise/search?q=${encodeURIComponent(query)}`).then(({ data }) => setResults(data.filter((f) => f.id !== dp?.franchise_partner_id))).catch(() => {});
      else setResults([]);
    }, 200);
    return () => clearTimeout(t);
  }, [query, dp]);

  if (!dp) return null;
  const backdated = effective < istToday();

  const submit = async () => {
    if (!fr) { toast.error("Select the new Franchise Partner"); return; }
    if (backdated && !confirmBackdated) { toast.error("Confirm the backdated transfer warning to proceed"); return; }
    setSaving(true);
    try {
      const { data } = await api.post(`/dp/${dp.id}/transfer`, {
        new_franchise_id: fr.id, effective_date: effective, reason,
        custom_reason: customReason, remarks, confirm_backdated: confirmBackdated,
      });
      toast.success(data.backdated
        ? `Transferred to ${fr.name} (backdated) — ${data.orders_reassigned} order(s) reassigned`
        : `Transferred to ${fr.name} effective ${fmtDate(data.effective_date)}`);
      onSaved?.();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Transfer failed");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg" data-testid="transfer-franchise-modal">
        <DialogHeader><DialogTitle className="flex items-center gap-2 font-head"><ArrowLeftRight className="h-5 w-5 text-primary" /> Delivery Partner Transfer</DialogTitle><DialogDescription className="sr-only">Transfer this Delivery Partner to a different Franchise Partner</DialogDescription></DialogHeader>

        <div className="grid grid-cols-2 gap-3 rounded-lg border border-border bg-secondary/40 p-3 text-sm">
          <div><p className="text-[11px] uppercase tracking-wider text-muted-foreground">Delivery Partner</p><p className="font-semibold" data-testid="transfer-dp-name">{dp.full_name}</p></div>
          <div><p className="text-[11px] uppercase tracking-wider text-muted-foreground">FE ID</p><p className="font-mono font-semibold" data-testid="transfer-fe-id">{dp.fe_id}</p></div>
          <div><p className="text-[11px] uppercase tracking-wider text-muted-foreground">Current Franchise</p><p className="font-semibold" data-testid="transfer-current-franchise">{dp.franchise_partner_name || "—"}</p></div>
          <div><p className="text-[11px] uppercase tracking-wider text-muted-foreground">Referral No.</p><p className="font-mono text-xs" data-testid="transfer-current-referral">{dp.franchise_referral || "—"}</p></div>
        </div>

        <div>
          <Label>New Franchise Partner</Label>
          {fr ? (
            <div className="flex items-center justify-between rounded-lg border border-emerald-500/40 bg-emerald-500/10 px-3 py-2 text-sm" data-testid="transfer-new-franchise">
              <span className="font-semibold">{fr.name} <span className="font-mono text-xs text-muted-foreground">{fr.referral_number}</span></span>
              <button className="text-xs text-rose-500" onClick={() => setFr(null)}>Change</button>
            </div>
          ) : (
            <>
              <Input placeholder="Search name / referral / city…" value={query} onChange={(e) => setQuery(e.target.value)} data-testid="transfer-franchise-search" />
              {results.length > 0 && (
                <div className="mt-1 max-h-36 overflow-y-auto rounded-lg border border-border bg-popover">
                  {results.map((x) => (
                    <button key={x.id} data-testid={`transfer-fr-opt-${x.referral_number}`} onClick={() => { setFr(x); setResults([]); setQuery(""); }}
                      className="flex w-full items-center justify-between px-3 py-2 text-left text-sm hover:bg-accent">
                      <span>{x.name} <span className="font-mono text-xs text-muted-foreground">{x.referral_number}</span></span>
                      <span className="text-xs text-muted-foreground">{x.city}</span>
                    </button>
                  ))}
                </div>
              )}
            </>
          )}
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>Transfer Effective Date</Label>
            <Input type="date" value={effective} onChange={(e) => setEffective(e.target.value)} data-testid="transfer-effective-date" />
            <p className="mt-1 text-[11px] text-muted-foreground">Effective from the start of this date (Asia/Kolkata). All activity on this date belongs to the new franchise.</p>
          </div>
          <div>
            <Label>Transfer Reason</Label>
            <Select value={reason} onValueChange={setReason}>
              <SelectTrigger data-testid="transfer-reason"><SelectValue /></SelectTrigger>
              <SelectContent>{REASONS.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
        {reason === "Other" && <div><Label>Specify Reason</Label><Input value={customReason} onChange={(e) => setCustomReason(e.target.value)} data-testid="transfer-custom-reason" /></div>}
        <div><Label>Remarks (optional)</Label><Textarea rows={2} value={remarks} onChange={(e) => setRemarks(e.target.value)} data-testid="transfer-remarks" /></div>

        {fr && (
          <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 text-xs text-amber-600 dark:text-amber-400" data-testid="transfer-confirm-text">
            Are you sure you want to transfer this Delivery Partner from <b>{dp.franchise_partner_name || "Unassigned"}</b> to <b>{fr.name}</b> effective from the selected date?
            All historical orders, earnings, payments and reports will remain associated with the original franchise for the period before the transfer.
          </div>
        )}
        {backdated && fr && (
          <label className="flex items-start gap-2 rounded-lg border border-rose-500/40 bg-rose-500/10 p-3 text-xs text-rose-600 dark:text-rose-400" data-testid="transfer-backdated-warning">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>This is a <b>backdated transfer</b>. Existing daily orders from {fmtDate(effective)} onward will be reassigned to {fr.name} and their franchise commission recalculated. Finalized payments and UTRs are never modified.
              <span className="mt-2 flex items-center gap-2 font-semibold"><Checkbox checked={confirmBackdated} onCheckedChange={setConfirmBackdated} data-testid="transfer-backdated-confirm" /> I confirm this backdated transfer and recalculation.</span>
            </span>
          </label>
        )}

        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} data-testid="transfer-cancel-btn">Cancel</Button>
          <Button onClick={submit} disabled={saving || !fr || (backdated && !confirmBackdated)} data-testid="transfer-confirm-btn">
            <ArrowLeftRight className="mr-1.5 h-4 w-4" />{saving ? "Transferring…" : "Confirm Transfer"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
